import React, { useState, useEffect, useContext, useRef } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../../contexts/AuthContext";
import FotoModelo, { tieneFoto } from "./FotoModelo";

// Alta y edición de un modelo (la usan Modelos y Entradas)
const API = process.env.REACT_APP_URL_POS ? process.env.REACT_APP_URL_POS.replace(/\/pos$/, "") : "";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` } });
const msg = (e) => (e.response && e.response.data && e.response.data.message) || "Intenta de nuevo";
export const VACIO = { codigo: "", nombre: "", lineas: "", marcas: "", colecciones: "", familias: "", precioTienda: "", costoMX: "", EAN: "", colores: [], tallas: [], is_active: "Si" };
const CATS = ["lineas", "marcas", "colecciones", "familias", "colores", "tallas"];
const SINGULAR = { lineas: "línea", marcas: "marca", colecciones: "colección", familias: "familia", colores: "color", tallas: "talla" };

// Botones que se prenden/apagan (colores, tallas)
export const Chips = ({ opciones, sel, onChange, onNuevo }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {opciones.filter((o) => o.is_active !== "No" || sel.includes(o._id)).map((o) => {
      const on = sel.includes(o._id);
      return (
        <button key={o._id} type="button" onClick={() => onChange(on ? sel.filter((x) => x !== o._id) : [...sel, o._id])}
          style={{ border: `1px solid ${on ? "#10b981" : "#e2e8f0"}`, background: on ? "#ecfdf5" : "#fff", color: "#0f172a", borderRadius: 999, padding: "4px 12px", fontSize: "0.85rem", fontWeight: on ? 700 : 400 }}>
          {on && <i className="fas fa-check mr-1" style={{ color: "#10b981" }} />}{o.name}
        </button>
      );
    })}
    {onNuevo && <button type="button" onClick={onNuevo} style={{ border: "1px dashed #94a3b8", background: "#fff", borderRadius: 999, padding: "4px 12px", fontSize: "0.85rem", color: "#64748b" }}>+ Nuevo</button>}
  </div>
);

// Modelo tal como lo manda GET /modelos (catálogos poblados) → valores del formulario
const aFormulario = (m) => ({
  ...VACIO, ...m,
  lineas: (m.lineas && m.lineas[0] && (m.lineas[0]._id || m.lineas[0])) || "",
  marcas: (m.marcas && m.marcas[0] && (m.marcas[0]._id || m.marcas[0])) || "",
  colecciones: (m.colecciones && m.colecciones[0] && (m.colecciones[0]._id || m.colecciones[0])) || "",
  familias: (m.familias && m.familias[0] && (m.familias[0]._id || m.familias[0])) || "",
  colores: (m.colores || []).map((x) => x._id || x), tallas: (m.tallas || []).map((x) => x._id || x),
  costoMX: m.costoMX !== undefined ? m.costoMX : "",
});

/*
 * modelo: null = cerrado · {} = nuevo · objeto = editar
 * inicial: valores para un modelo nuevo (ej. el código que se escribió en Entradas)
 * onGuardado(modelo): ya guardado (con foto subida si se eligió una)
 * onCatalogo(): se agregó una línea, color, talla… (para refrescar listas de afuera)
 */
function ModeloModal({ modelo, inicial, onCerrar, onGuardado, onCatalogo }) {
  const { user } = useContext(AuthContext);
  const verCostos = !!(user && user.posCostos);
  const [form, setForm] = useState(null);
  const [cat, setCat] = useState(Object.fromEntries(CATS.map((k) => [k, []])));
  const [guardando, setGuardando] = useState(false);
  const [foto, setFoto] = useState(null); // { archivo, vista } nueva foto elegida
  const [quitarFoto, setQuitarFoto] = useState(false);
  const inputFoto = useRef(null);

  const cargarCat = () => axios.get(`${API}/modelos/catalogos`, auth()).then((r) => setCat(r.data)).catch(() => {});

  useEffect(() => {
    if (!modelo) { setForm(null); return; }
    setForm(modelo._id ? aFormulario(modelo) : { ...VACIO, ...(inicial || {}) });
    setFoto(null); setQuitarFoto(false);
    cargarCat();
  }, [modelo]); // eslint-disable-line

  useEffect(() => () => { if (foto) URL.revokeObjectURL(foto.vista); }, [foto]);

  const elegirFoto = (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\//.test(f.type)) return Swal.fire({ icon: "warning", title: "Elige una imagen (jpg, png o webp)" });
    if (f.size > 8 * 1024 * 1024) return Swal.fire({ icon: "warning", title: "La foto pesa más de 8 MB" });
    setFoto({ archivo: f, vista: URL.createObjectURL(f) });
    setQuitarFoto(false);
  };

  const nuevoEnCatalogo = async (tipo, alCrear) => {
    const r = await Swal.fire({ title: `Nueva ${SINGULAR[tipo]}`, input: "text", showCancelButton: true, confirmButtonText: "Agregar", cancelButtonText: "Cancelar", inputValidator: (v) => (!v || !v.trim() ? "Escribe el nombre" : undefined) });
    if (!r.value) return;
    try {
      const x = (await axios.post(`${API}/modelos/catalogo/${tipo}`, { name: r.value }, auth())).data;
      await cargarCat();
      if (onCatalogo) onCatalogo();
      if (alCrear) alCrear(x._id);
    } catch (e) { Swal.fire({ icon: "error", title: "No se agregó", text: msg(e) }); }
  };

  const guardar = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) return Swal.fire({ icon: "warning", title: "Pon código y nombre" });
    setGuardando(true);
    try {
      const datos = { ...form, costoMX: form.costoMX === "" ? 0 : form.costoMX };
      delete datos.urlFoto; delete datos.existencia;
      if (!verCostos && form._id) delete datos.costoMX;
      const m = form._id ? (await axios.patch(`${API}/modelos/${form._id}`, datos, auth())).data : (await axios.post(`${API}/modelos`, datos, auth())).data;
      let urlFoto = form.urlFoto;
      try {
        if (foto) {
          const fd = new FormData();
          fd.append("file", foto.archivo);
          urlFoto = (await axios.post(`${API}/modelos/${m._id}/foto`, fd, auth())).data.urlFoto;
        } else if (quitarFoto && form._id) {
          await axios.delete(`${API}/modelos/${m._id}/foto`, auth());
          urlFoto = "No";
        }
      } catch (e) {
        await Swal.fire({ icon: "warning", title: "El modelo se guardó, la foto no", text: msg(e) });
      }
      Swal.fire({ icon: "success", title: "Modelo guardado", timer: 1300, showConfirmButton: false });
      if (onGuardado) onGuardado({ ...m, urlFoto });
    } catch (e) { Swal.fire({ icon: "error", title: "No se guardó", text: msg(e) }); } finally { setGuardando(false); }
  };

  const sel = (k) => (
    <Input type="select" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}>
      <option value="">—</option>
      {cat[k].filter((o) => o.is_active !== "No" || o._id === form[k]).map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
    </Input>
  );

  const vistaFoto = foto ? foto.vista : !quitarFoto && form && tieneFoto(form.urlFoto) ? form.urlFoto : null;

  return (
    <Modal isOpen={!!modelo} toggle={onCerrar} size="lg">
      <ModalHeader toggle={onCerrar}>{form && form._id ? `Editar ${form.codigo}` : "Nuevo modelo"}</ModalHeader>
      {form && (
        <ModalBody>
          <div className="row">
            <div className="col-md-3 mb-3">
              <label>Foto</label>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                {vistaFoto
                  ? <img src={vistaFoto} alt="" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  : <FotoModelo url={null} size={120} />}
                <input ref={inputFoto} type="file" accept="image/*" style={{ display: "none" }} onChange={elegirFoto} />
                <div>
                  <Button size="sm" color="primary" outline onClick={() => inputFoto.current.click()}><i className="fas fa-camera mr-1" />{vistaFoto ? "Cambiar" : "Subir foto"}</Button>{" "}
                  {vistaFoto && <Button size="sm" color="danger" outline onClick={() => { setFoto(null); setQuitarFoto(true); }} title="Quitar foto"><i className="fas fa-trash" /></Button>}
                </div>
              </div>
            </div>
            <div className="col-md-9">
              <div className="row">
                <div className="col-md-5 mb-3"><label>Código</label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="NF-BL-001" /></div>
                <div className="col-md-7 mb-3"><label>Nombre</label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Blusa satinada cuello V" /></div>
                <div className="col-6 mb-3"><label>Línea</label>{sel("lineas")}</div>
                <div className="col-6 mb-3"><label>Marca</label>{sel("marcas")}</div>
                <div className="col-6 mb-3"><label>Colección</label>{sel("colecciones")}</div>
                <div className="col-6 mb-3"><label>Familia</label>{sel("familias")}</div>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-3"><label>Precio de tienda</label><Input type="number" min="0" step="0.01" value={form.precioTienda} onChange={(e) => setForm({ ...form, precioTienda: e.target.value })} /></div>
            {(verCostos || !form._id) && <div className="col-6 col-md-3 mb-3"><label>Costo de referencia</label><Input type="number" min="0" step="0.01" value={form.costoMX} onChange={(e) => setForm({ ...form, costoMX: e.target.value })} /></div>}
            <div className="col-md-6 mb-3"><label>Código de barras del proveedor (opcional)</label><Input value={form.EAN} onChange={(e) => setForm({ ...form, EAN: e.target.value })} /></div>
            <div className="col-12 mb-3">
              <label>Colores</label>
              <Chips opciones={cat.colores} sel={form.colores} onChange={(v) => setForm({ ...form, colores: v })} onNuevo={() => nuevoEnCatalogo("colores", (id) => setForm((f) => ({ ...f, colores: [...f.colores, id] })))} />
            </div>
            <div className="col-12 mb-3">
              <label>Tallas</label>
              <Chips opciones={cat.tallas} sel={form.tallas} onChange={(v) => setForm({ ...form, tallas: v })} onNuevo={() => nuevoEnCatalogo("tallas", (id) => setForm((f) => ({ ...f, tallas: [...f.tallas, id] })))} />
              <div className="small text-muted mt-1">Sin colores ni tallas (bolsas, accesorios) se vende como una sola pieza. {form.colores.length > 0 && form.tallas.length > 0 ? `${form.colores.length * form.tallas.length} combinaciones.` : ""}</div>
            </div>
            {form._id && (
              <div className="col-md-4 mb-2"><label>Estado</label>
                <Input type="select" value={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.value })}>
                  <option value="Si">Activo</option><option value="No">Inactivo (ya no se vende)</option>
                </Input>
              </div>
            )}
          </div>
        </ModalBody>
      )}
      <ModalFooter>
        <Button color="secondary" onClick={onCerrar}>Cancelar</Button>
        <Button color="success" disabled={guardando} onClick={guardar}>{guardando ? <i className="fas fa-spinner fa-spin" /> : "Guardar"}</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ModeloModal;
